import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import ReelCropSettings from './ReelCropSettings';
import TimestampInput from './TimestampInput';
import PasteClipButton from './PasteClipButton';
import {
  archiveReel,
  createSessionReels,
  fetchSessionReels,
  updateReelMetadata,
} from '../../services/training';
import { DEFAULT_CROP_SETTINGS } from '../../utils/reelCrop';
import { formatTimestamp, parseTimestampInput } from '../../utils/videoTime';

const EMPTY_CLIP = { id: null, start: '', end: '', caption: '', ...DEFAULT_CROP_SETTINGS };

function FieldLabel({ children }) {
  return (
    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
      {children}
    </label>
  );
}

function TextInput({ className = '', ...props }) {
  return (
    <input
      className={`w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-all ${className}`}
      {...props}
    />
  );
}

function reelsToRows(reels) {
  if (!reels?.length) return [{ ...EMPTY_CLIP }];
  return reels.map((reel) => ({
    id: reel.id,
    start: reel.clipStartSeconds != null ? formatTimestamp(reel.clipStartSeconds) : '',
    end: reel.clipEndSeconds != null ? formatTimestamp(reel.clipEndSeconds) : '',
    caption: reel.caption || '',
    cropMode: reel.cropMode ?? DEFAULT_CROP_SETTINGS.cropMode,
    focalX: reel.focalX ?? DEFAULT_CROP_SETTINGS.focalX,
    focalY: reel.focalY ?? DEFAULT_CROP_SETTINGS.focalY,
  }));
}

export default function SessionClipsPanel({
  session,
  canEdit,
  videoRef,
  embedded = false,
}) {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([{ ...EMPTY_CLIP }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadReels = useCallback(async () => {
    if (!session?.id) return [];
    const list = await fetchSessionReels(session.id);
    setReels(list);
    return list;
  }, [session?.id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadReels()
      .catch((err) => {
        if (active) setError(err.message || 'No se pudieron cargar los clips');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [loadReels]);

  useEffect(() => {
    if (!editing) {
      setRows(reelsToRows(reels));
    }
  }, [reels, editing]);

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const updateRowCrop = (index, crop) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...crop } : row)));
  };

  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_CLIP }]);

  const removeRow = (index) => {
    setRows((prev) => (prev.length <= 1 ? [{ ...EMPTY_CLIP }] : prev.filter((_, i) => i !== index)));
  };

  const handleCancel = () => {
    setEditing(false);
    setError('');
    setRows(reelsToRows(reels));
  };

  const seekTo = (seconds) => {
    const video = videoRef?.current;
    if (!video || seconds == null) return;
    video.currentTime = seconds;
    video.play().catch(() => {});
  };

  const parseRows = () => {
    const parsed = [];
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      if (!row.start?.trim() && !row.end?.trim() && !row.caption?.trim() && !row.id) continue;
      const start = parseTimestampInput(row.start, `Inicio del clip ${i + 1}`);
      const end = parseTimestampInput(row.end, `Fin del clip ${i + 1}`);
      if (end <= start) {
        throw new Error(`El fin del clip ${i + 1} debe ser posterior al inicio`);
      }
      if (session.durationSeconds && end > session.durationSeconds) {
        throw new Error(
          `El clip ${i + 1} no puede terminar después de ${formatTimestamp(session.durationSeconds)}`
        );
      }
      parsed.push({
        id: row.id,
        clipStartSeconds: start,
        clipEndSeconds: end,
        caption: row.caption.trim() || session.title || 'Clip',
        cropMode: row.cropMode,
        focalX: row.focalX,
        focalY: row.focalY,
      });
    }
    return parsed;
  };

  const handleSave = async () => {
    setError('');
    let parsed;
    try {
      parsed = parseRows();
    } catch (err) {
      setError(err.message);
      return;
    }

    if (!session.publitioVideoId || !session.videoUrl) {
      setError('Esta sesión no tiene un video vinculado para crear clips');
      return;
    }

    setSaving(true);
    try {
      const keptIds = new Set(parsed.filter((c) => c.id).map((c) => c.id));
      const toRemove = reels.filter((r) => !keptIds.has(r.id));
      const toUpdate = parsed.filter((c) => c.id);
      const toCreate = parsed.filter((c) => !c.id);

      await Promise.all(toRemove.map((r) => archiveReel(r.id)));

      await Promise.all(
        toUpdate.map((clip) =>
          updateReelMetadata(clip.id, {
            caption: clip.caption,
            clipStartSeconds: clip.clipStartSeconds,
            clipEndSeconds: clip.clipEndSeconds,
          })
        )
      );

      if (toCreate.length > 0) {
        await createSessionReels({
          sessionId: session.id,
          publitioVideoId: session.publitioVideoId,
          videoUrl: session.videoUrl,
          thumbnailUrl: session.thumbnailUrl || null,
          clips: toCreate,
        });
      }

      const list = await loadReels();
      setRows(reelsToRows(list));
      setEditing(false);
    } catch (err) {
      setError(err.message || 'No se pudieron guardar los clips');
    } finally {
      setSaving(false);
    }
  };

  const padX = embedded ? 'px-0' : 'px-4 md:px-6';

  if (!canEdit) return null;

  if (loading && reels.length === 0) {
    return (
      <div className={`${padX} pt-1 text-xs text-[var(--text-secondary)] font-bold uppercase tracking-widest`}>
        Cargando clips…
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'bg-[var(--bg-base)]'}>
      {canEdit && (
        <div className={`${padX} ${embedded ? 'pt-0' : 'pt-4'} flex items-center justify-between gap-4`}>
          <p className="text-xs text-[var(--text-secondary)]">
            {reels.length
              ? `${reels.length} clip${reels.length === 1 ? '' : 's'} / reel${reels.length === 1 ? '' : 's'} de esta sesión`
              : 'Sin clips — crea reels a partir de tramos de este video'}
          </p>
          {!editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-brand-cyan hover:text-brand-green flex-shrink-0"
            >
              <SafeIcon name="Pencil" className="w-3.5 h-3.5" />
              {reels.length ? 'Editar clips' : 'Añadir clips'}
            </button>
          ) : (
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-main)]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 gfi-gradient-bg text-brand-navy px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <SafeIcon name="Loader" className="w-3.5 h-3.5 animate-spin" />
                    Guardando…
                  </>
                ) : (
                  'Guardar'
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className={`${padX} mt-3 p-3 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm`}>
          {error}
        </div>
      )}

      {editing ? (
        <div className={`${padX} py-4 space-y-4`}>
          {rows.map((row, index) => (
            <div
              key={row.id || `new-${index}`}
              className="space-y-3 p-4 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-subtle)]"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]">
                  Clip {index + 1}
                  {row.id ? '' : ' (nuevo)'}
                </span>
                <div className="flex items-center gap-3 shrink-0">
                  <PasteClipButton
                    disabled={saving}
                    onParsed={({ title, start, end }) => {
                      setRows((prev) =>
                        prev.map((r, i) =>
                          i === index
                            ? { ...r, caption: title, start, end }
                            : r
                        )
                      );
                    }}
                  />
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      disabled={saving}
                      className="text-red-400 text-[10px] font-bold uppercase"
                    >
                      Quitar
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Inicio</FieldLabel>
                  <TimestampInput
                    value={row.start}
                    onChange={(e) => updateRow(index, 'start', e.target.value)}
                    disabled={saving}
                  />
                </div>
                <div>
                  <FieldLabel>Fin</FieldLabel>
                  <TimestampInput
                    value={row.end}
                    onChange={(e) => updateRow(index, 'end', e.target.value)}
                    disabled={saving}
                  />
                </div>
              </div>
              <div>
                <FieldLabel>Caption del reel</FieldLabel>
                <TextInput
                  value={row.caption}
                  onChange={(e) => updateRow(index, 'caption', e.target.value)}
                  disabled={saving}
                  placeholder="Opcional — usa el título de la sesión si está vacío"
                />
              </div>
              {session.videoUrl && !row.id && (
                <ReelCropSettings
                  videoSrc={session.videoUrl}
                  aspectRatio={null}
                  value={{
                    cropMode: row.cropMode,
                    focalX: row.focalX,
                    focalY: row.focalY,
                  }}
                  onChange={(crop) => updateRowCrop(index, crop)}
                  disabled={saving}
                  compact
                />
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            disabled={saving}
            className="text-[10px] font-black uppercase tracking-widest text-brand-cyan hover:text-brand-green"
          >
            + Otro clip
          </button>
          <p className="text-xs text-[var(--text-secondary)]">
            Los reels usan el mismo video; indica inicio y fin en mm:ss. Quitar un clip existente lo archiva del feed.
          </p>
        </div>
      ) : (
        reels.length > 0 && (
          <ul className={`${padX} py-4 space-y-2`}>
            {reels.map((reel) => (
              <li
                key={reel.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface)]"
              >
                <button
                  type="button"
                  onClick={() => seekTo(reel.clipStartSeconds)}
                  className="shrink-0 w-10 h-10 rounded-full border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] hover:border-brand-cyan"
                  aria-label="Ir al inicio del clip"
                >
                  <SafeIcon name="Play" className="w-4 h-4" />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[var(--text-main)] truncate">
                    {reel.caption || session.title}
                  </p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)] mt-0.5">
                    {formatTimestamp(reel.clipStartSeconds ?? 0)}
                    {' — '}
                    {formatTimestamp(reel.clipEndSeconds ?? 0)}
                  </p>
                </div>
                {canEdit && (
                  <Link
                    to={`/app/reels/${reel.id}/editar`}
                    className="text-[10px] font-black uppercase tracking-widest text-brand-cyan hover:text-brand-green shrink-0"
                  >
                    Editar
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}
